import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classes from "./ThemeSelectComponent.scss";
import { themeIcon } from "./assets/SVGs";
import EmptyField from "UIComponents/EmptyField";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import classNames from "classnames";
import { TickCircleFilled } from "@toddle-edu/ds-icons";
import { onEnterOrSpaceKeyDown } from "Utils";
import { CardSelection } from "@toddle-edu/ds-web";

const styles = {
  cardSelectionIconStyles: {
    width: "56px",
    height: "56px",
  },
  cardSelectionStyles: {
    alignItems: "center",
  },
  cardSelectionTitleStyles: {
    height: "56px",
    alignItems: "center",
    display: "flex",
  },
};

class ThemeSelectComponent extends UIBaseComponent {
  onClickTheme = value => {
    const { options } = this.props;

    if (this.props.value != value) {
      this.updateValue(value, { nodes: options });
    } else {
      this.updateValue("", { nodes: options });
    }
  };

  getThemeList = () => {
    const {
      options,
      value,
      emptyText,
      isDisabled,
      displayDisabledAsActive,
      testId,
      useCardSelection = false,
    } = this.props;
    const { error } = this.state;

    /*******************************************************************************************************
     * displayDisabledAsActive flag is used to control the visual state of this component,
     * where if it is true, we will show the element in visually active form but
     * functionality wise it will behave like disabled
     ********************************************************************************************************/

    if (_.isEmpty(options)) {
      return <EmptyField title={emptyText} />;
    }

    return _.map(options, theme => {
      const isSelected = value == theme.value;

      const themeSvg = themeIcon({
        isSelected,
        metadata: theme.metadata,
        themeLabel: theme.label,
      });

      const containerStyle = classNames(
        { [classes.themeComponent]: !isSelected },
        { [classes.themeComponentActive]: isSelected },
        { [classes.themeComponentDisabled]: isDisabled },
        {
          [classes.displayDisabledAsActive]:
            isDisabled && displayDisabledAsActive,
        }
      );
      const themeContainerStyle = classNames(
        { [classes.themeIcon]: true },
        { [classes.error]: !!error }
      );

      const onClickThemeCallBack = () => {
        if (!isDisabled) this.onClickTheme(theme.value);
      };

      const descriptor = theme?.node?.descriptor;

      if (useCardSelection) {
        return (
          <CardSelection
            value={theme.value}
            prefixIcon={
              <div style={styles.cardSelectionIconStyles}>{themeSvg}</div>
            }
            mode="view"
            disabled={isDisabled}
            key={theme.value}
            onCardSelectionClick={onClickThemeCallBack}
            type="radio"
            title={
              <div style={styles.cardSelectionTitleStyles}>{theme.label}</div>
            }
            selectedValues={[value]}
            style={styles.cardSelectionStyles}
            testId={`${testId}-${theme.value}`}
          />
        );
      }

      return (
        <div
          key={theme.value}
          className={containerStyle}
          onClick={onClickThemeCallBack}
          onKeyDown={e => {
            onEnterOrSpaceKeyDown(e, onClickThemeCallBack);
          }}
          data-test-id={`${testId}-${theme.value}-themeCard`}
          data-is-selected={isSelected}
        >
          <div className={themeContainerStyle} tabIndex={0}>
            {isSelected ? (
              <div className={classes.selectedTickComp}>
                <TickCircleFilled variant="link" size="x-small" />
              </div>
            ) : null}
            <div className={classes.themeSvg}>{themeSvg}</div>
          </div>
          <div
            className={classNames("text-body text-primary", classes.themeLabel)}
          >
            {theme.label}
          </div>
          {_.size(descriptor) > 0 && !_.includes(descriptor, ";") ? (
            <div className={classNames("text-body-s", classes.themeDescriptor)}>
              {descriptor}
            </div>
          ) : null}
        </div>
      );
    });
  };

  renderEdit = () => {
    const { dsVersion } = this.props;
    const isDSV2 = dsVersion === "2.0";
    const themeList = this.getThemeList();

    const componentContainerClasses = classNames({
      [classes.componentContainer]: true,
      [classes.componentContainerV2]: isDSV2,
    });

    const themeListCompClasses = classNames({
      [classes.themeListComp]: !isDSV2,
      [classes.themeListCompV2]: isDSV2,
    });

    return (
      <div className={componentContainerClasses}>
        <div className={themeListCompClasses}>{themeList}</div>
      </div>
    );
  };
}

ThemeSelectComponent.propTypes = {
  ...UIBaseComponent.propTypes,
  options: PropTypes.array,
  isDisabled: PropTypes.bool,
  displayDisabledAsActive: PropTypes.bool,
};

ThemeSelectComponent.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  isDisabled: false,
  displayDisabledAsActive: false,
};

export default ThemeSelectComponent;
